import React from 'react'
import { Link } from 'react-router-dom'
import { Tooltip, Typography, Avatar } from '@mui/material'
import * as ROUTES from '@/config'

/**
 * DatagridUser
 * Lightweight presentational component for rendering a user cell inside MUI DataGrid / tables.
 * - Shows avatar + primary text (name or email) + secondary (email if name shown)
 * - Optionally wraps content in a link to learner profile (UserSessionDetail page)
 * - Designed to be compact & safe for missing data
 */
export interface DatagridUserProps {
  /** User object (partial). Accepts flexible keys. */
  user: Partial<{
    _id: string
    email: string
    first_name: string
    last_name: string
    profile_image: string
    subtitle?: string
  }> &
    Record<string, any>
  /** Show avatar circle (default true) */
  showAvatar?: boolean
  /** Wrap in link to learner profile (default true) */
  linkToProfile?: boolean
  /** Override link builder if custom destination needed */
  buildLink?: (user: DatagridUserProps['user']) => string | null
  /** Size of avatar in px (default 32) */
  avatarSize?: number
  /** Optional className for root wrapper */
  className?: string
  /** If true, show tooltip with full name/email (default true) */
  showTooltip?: boolean
  /** If true, render both name and email; otherwise only one line (default false) */
  showBoth?: boolean
}

const getDisplayName = (u: DatagridUserProps['user']) => {
  return (
    [u.first_name, u.last_name].filter(Boolean).join(' ').trim() ||
    u.email ||
    ''
  )
}

const getSecondary = (u: DatagridUserProps['user']) => {
  if (u.subtitle) return u.subtitle
  const display = getDisplayName(u)
  if (u.email && u.email !== display) return u.email
  return ''
}

const resolveAvatarSrc = (u: DatagridUserProps['user']) => {
  const raw = (u.profile_image || '').trim()
  if (!raw) return ''
  if (raw.startsWith('https')) return raw

  const win: any = typeof window !== 'undefined' ? window : {}
  const base: string = (win && win.MEDIA_BASE_URL) || ''
  if (!base) return raw

  const needsSlash = !base.endsWith('/') && !raw.startsWith('/')
  const stripDoubleSlash = base.endsWith('/') && raw.startsWith('/')

  return stripDoubleSlash
    ? `${base}${raw.substring(1)}`
    : needsSlash
      ? `${base}/${raw}`
      : `${base}${raw}`
}

const buildDefaultProfileLink = (u: DatagridUserProps['user']) => {
  const userId = u._id

  if (!userId) return null
  return ROUTES.USAGETRENDS_USERSESSIONS_EMAILORUSERNAME.replace(
    ':userIdOrEmail',
    userId || u.email,
  )
}

export const DatagridUser: React.FC<DatagridUserProps> = ({
  user,
  showAvatar = false,
  linkToProfile = true,
  buildLink,
  avatarSize = 28,
  className = '',
  showTooltip = true,
  showBoth = false,
}) => {
  const name = getDisplayName(user)
  // Only compute secondary when needed (for tooltip or when rendering both)
  const secondary = showBoth || showTooltip ? getSecondary(user) : ''
  const avatarSrc = resolveAvatarSrc(user)
  const profileLink = linkToProfile
    ? buildLink
      ? buildLink(user)
      : buildDefaultProfileLink(user)
    : null

  const content = (
    <div
      className={`tw-flex tw-items-center tw-gap-3 ${className}`.trim()}
      style={{ minWidth: 0 }}
    >
      {showAvatar && (
        <Avatar
          src={avatarSrc}
          sx={{ width: avatarSize, height: avatarSize, flexShrink: 0 }}
          alt={name || user.email || '—'}
        />
      )}

      <div className="flex flex-col leading-tight" style={{ minWidth: 0 }}>
        <Typography
          variant="body3Link"
          className="break-words"
          sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
        >
          {name || user.email || '—'}
        </Typography>

        {showBoth && secondary && (
          <Typography
            variant="caption"
            className="!text-gray-500 break-words"
            data-testid="dg-user-secondary"
            lineHeight={1.1}
            sx={{ wordBreak: 'break-word', overflowWrap: 'break-word' }}
          >
            {secondary}
          </Typography>
        )}
      </div>
    </div>
  )

  const maybeTooltip = (child: React.ReactElement) =>
    showTooltip && (name || secondary) ? (
      <Tooltip title={[name, secondary].filter(Boolean).join(' • ')}>
        {child}
      </Tooltip>
    ) : (
      child
    )

  if (profileLink) {
    return maybeTooltip(
      <Link
        to={profileLink}
        className="no-underline hover:tw-no-underline"
        onClick={e => {
          // Prevent navigation if no user id resolved
          if (!user._id) e.preventDefault()
        }}
      >
        {content}
      </Link>,
    )
  }

  return maybeTooltip(content as React.ReactElement)
}

export default DatagridUser
